import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtEUR } from "../lib/format";
import { RefreshCcw, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ImpactBanner from "../components/app/ImpactBanner";
import MorningBriefCard from "../components/app/MorningBriefCard";
import {
  Card, Button, SectionLabel,
} from "../components/app/theme";

/* ── Metric épuré (chiffres du haut) ──────────────────────────── */
function Metric({ label, value, hint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="th-metric__label">{label}</div>
      <div className="th-metric__value">{value}</div>
      {hint && <div className="th-meta mt-2">{hint}</div>}
    </div>
  );
}

/* ── Ligne priorisée ──────────────────────────────────────────── */
function PriorityRow({ amount, name, meta, testid }) {
  return (
    <div className="th-prio-row" data-testid={testid}>
      <div className="th-prio-row__amount">{amount}</div>
      <div className="min-w-0">
        <div className="th-prio-row__name">{name}</div>
        <div className="th-prio-row__meta">{meta}</div>
      </div>
    </div>
  );
}

/* ── Section liste (À relancer / Clients qui décrochent) ──────── */
function PrioritySection({ title, rows, total, seeAllLabel, seeAllTo, emptyLabel, testid }) {
  const visible = rows.slice(0, 5);
  const hasMore = total > visible.length;

  return (
    <section data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <h2
          className="text-[18px] font-medium"
          style={{ color: "var(--th-text)", letterSpacing: "-0.005em" }}
        >
          {title}
        </h2>
        <div className="th-meta">
          {total} au total
        </div>
      </div>
      <Card>
        {visible.length === 0 && (
          <div className="p-8 text-center text-sm" style={{ color: "var(--th-text-3)" }}>
            {emptyLabel}
          </div>
        )}
        {visible.map((r) => (
          <PriorityRow key={r.key} amount={r.amount} name={r.name} meta={r.meta} testid={r.testid} />
        ))}
      </Card>
      {hasMore && (
        <div className="mt-3 text-right">
          <Link to={seeAllTo} className="th-link inline-flex items-center gap-1.5">
            {seeAllLabel} <ArrowRight size={13} />
          </Link>
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
    } catch {
      toast.error("Erreur de chargement du tableau de bord");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-8 text-sm" style={{ color: "var(--th-text-3)" }}>Chargement…</div>;
  }
  if (!data) return null;

  /* ── Consolidation propales à relancer : tri montant décroissant ── */
  const allActivePropales = [
    ...(data.risques?.HAUT?.propales || []),
    ...(data.risques?.MOYEN?.propales || []),
    ...(data.risques?.FAIBLE?.propales || []),
  ].sort((a, b) => (b.montant_ht || 0) - (a.montant_ht || 0));

  const relanceRows = allActivePropales.map((p) => ({
    key: `p-${p.id}`,
    testid: `dash-relance-row-${p.id}`,
    amount: fmtEUR(p.montant_ht),
    name: p.client,
    meta: `${p._score_info?.metrics?.jours_sans_reponse ?? "—"} j sans réponse · ${p.nb_relances} relance${p.nb_relances > 1 ? "s" : ""}`,
  }));
  const totalRelance = data.kpi?.encours_nombre ?? relanceRows.length;

  /* ── Clients qui décrochent : tri CA en jeu décroissant ─────── */
  const dormantsSorted = [...(data.top_dormants || [])]
    .sort((a, b) => (b.ca_annuel || 0) - (a.ca_annuel || 0));

  const dormantRows = dormantsSorted.map((d) => ({
    key: `c-${d.client?.id || d.client?.nom}`,
    testid: `dash-dormant-row-${d.client?.id || d.client?.nom}`,
    amount: fmtEUR(d.ca_annuel),
    name: d.client?.nom,
    meta: `Silence ${d.silence_jours ?? "—"} j · cycle habituel ${d.cycle_jours ?? "—"} j`,
  }));
  const totalDormants = data.kpi?.nb_clients_dormants ?? dormantRows.length;

  return (
    <div className="th-scope p-8 md:p-12 max-w-[1200px]" style={{ backgroundColor: "var(--th-bg)" }}>
      <header className="mb-10 flex items-start justify-between gap-4">
        <div>
          <SectionLabel>Vue dirigeant</SectionLabel>
          <h1
            className="mt-2 text-[27px] font-medium leading-tight"
            style={{ color: "var(--th-text)", letterSpacing: "-0.005em" }}
          >
            L&apos;argent qui dort
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--th-text-2)" }}>
            Indicateurs financiers, sans mesure d&apos;activité individuelle.
          </p>
        </div>
        <Button onClick={load} variant="secondary">
          <RefreshCcw size={14} /> Actualiser
        </Button>
      </header>

      <MorningBriefCard />

      <ImpactBanner impact={data.impact} size="large" />

      {/* 3 indicateurs */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-14">
        <Metric
          testid="kpi-encours"
          label="Encours à relancer"
          value={fmtEUR(data.kpi.encours_montant)}
          hint={`${data.kpi.encours_nombre} propale${data.kpi.encours_nombre > 1 ? "s" : ""} en attente`}
        />
        <Metric
          testid="kpi-ca-dormant"
          label="CA dormant détecté"
          value={fmtEUR(data.kpi.ca_dormant)}
          hint={`${data.kpi.nb_clients_dormants} client${data.kpi.nb_clients_dormants > 1 ? "s" : ""} au cycle cassé`}
        />
        <Metric
          testid="kpi-potentiel"
          label="Potentiel récupérable"
          value={fmtEUR(data.kpi.potentiel_recuperable)}
          hint="Propales à haut risque et clients dormants"
        />
      </section>

      {/* 2 listes hiérarchisées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <PrioritySection
          testid="dash-section-relance"
          title="À relancer en priorité"
          rows={relanceRows}
          total={totalRelance}
          emptyLabel="Rien à relancer aujourd&apos;hui."
          seeAllLabel={`Voir les ${totalRelance}`}
          seeAllTo="/propales"
        />
        <PrioritySection
          testid="dash-section-dormants"
          title="Clients qui décrochent"
          rows={dormantRows}
          total={totalDormants}
          emptyLabel="Aucun client au cycle cassé."
          seeAllLabel="Voir tout"
          seeAllTo="/clients"
        />
      </div>
    </div>
  );
}
